// Branded terminal states for routing failures. Before this existed, any
// unknown URL — stale bookmark, mistyped path, removed route — rendered
// React Router's raw developer error screen ("Hey developer 👋") in
// production. HIG forgiveness: a bad URL must land somewhere recognizable
// with a way out, never a dead end.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { ArrowLeft, Compass, Home } from 'lucide-react';
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router-dom';
function TerminalShell({ title, description, children }) {
    const navigate = useNavigate();
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-6 text-center",
        "data-testid": Testids.pageState.error,
        children: [
            /*#__PURE__*/ _jsx(Compass, {
                className: "h-10 w-10 text-muted-foreground"
            }),
            /*#__PURE__*/ _jsx("h1", {
                className: "text-2xl font-semibold",
                children: title
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "max-w-md text-sm text-muted-foreground",
                children: description
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "mt-2 flex gap-2",
                children: [
                    /*#__PURE__*/ _jsxs(SpecraButton, {
                        variant: "secondary",
                        onClick: ()=>navigate(-1),
                        children: [
                            /*#__PURE__*/ _jsx(ArrowLeft, {
                                className: "mr-2 h-4 w-4"
                            }),
                            "Go back"
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(SpecraButton, {
                        onClick: ()=>navigate('/dashboard'),
                        children: [
                            /*#__PURE__*/ _jsx(Home, {
                                className: "mr-2 h-4 w-4"
                            }),
                            "Dashboard"
                        ]
                    })
                ]
            }),
            children
        ]
    });
}
export function NotFoundPage() {
    return /*#__PURE__*/ _jsx(TerminalShell, {
        title: "This page doesn't exist",
        description: "The link may be out of date, or the page may have moved. Your work is safe — head back or start from the dashboard."
    });
}
/**
 * Root errorElement: catches both 404s (no matching route) and errors thrown
 * during routing/rendering, replacing React Router's default developer
 * screen. Render errors keep a hint for bug reports without leaking a stack.
 */ export function RouteErrorPage() {
    const error = useRouteError();
    if (isRouteErrorResponse(error) && error.status === 404) {
        return /*#__PURE__*/ _jsx(NotFoundPage, {});
    }
    const detail = error instanceof Error ? error.message : undefined;
    return /*#__PURE__*/ _jsx(TerminalShell, {
        title: "Something went wrong",
        description: "The page hit an unexpected error. Going back or reloading usually resolves it — if it keeps happening, report it with the note below.",
        children: detail && /*#__PURE__*/ _jsx("p", {
            className: "mt-4 max-w-md break-words rounded-md bg-muted px-3 py-2 font-mono text-xs text-muted-foreground",
            children: detail
        })
    });
}
